import React, { useEffect, useState, useRef } from "react";
import Filter from "./Filter";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import { ReactComponent as MapPinIcon } from "../../../assets/Icons/map-pin.svg";
import { useCommon } from "../../../contexts/common";

const LicensedInFilter = ({ setFilters, filters }) => {
  const { statesList, fetchStatesList } = useCommon();
  const [selectedStates, setSelectedStates] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [tempSelectedStates, setTempSelectedStates] = useState([]);
  const containerRef = useRef(null);
  const paperRef = useRef(null);

  useEffect(() => {
    const loadStates = async () => {
      await fetchStatesList();
    };
    loadStates();
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (
      filters?.licensed_in &&
      Array.isArray(filters.licensed_in) &&
      statesList.length > 0
    ) {
      const selected = statesList.filter((state) => {
        return filters.licensed_in?.includes(state.id.toString());
      });
      setSelectedStates(selected);
      setTempSelectedStates(selected);
    } else if (!filters?.licensed_in || filters.licensed_in.length === 0) {
      setSelectedStates([]);
      setTempSelectedStates([]);
    }
  }, [statesList.length, filters?.licensed_in]);

  // Handle outside clicks
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen) {
        // Check if click is outside the container AND outside the dropdown paper
        const isOutsideContainer =
          containerRef.current && !containerRef.current.contains(event.target);
        const isOutsidePaper =
          paperRef.current && !paperRef.current.contains(event.target);

        if (isOutsideContainer && isOutsidePaper) {
          // Clicked outside - revert to previously applied filters and close
          setTempSelectedStates(selectedStates);
          setIsOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, selectedStates]);

  const handleOpen = () => {
    setTempSelectedStates(selectedStates);
    setIsOpen(true);
  };

  const handleApply = () => {
    setSelectedStates(tempSelectedStates);
    setFilters((prev) => ({
      ...prev,
      licensed_in: tempSelectedStates.map((state) => state.id.toString()),
      page: 1,
    }));
    setIsOpen(false);
  };

  const handleTempChange = (event, newValue, reason, details) => {
    // Handle chip removal
    if (reason === "removeOption" && details?.option) {
      const removedOption = details.option;
      
      // Check if this option was already applied (exists in selectedStates)
      const wasApplied = selectedStates.some(state => state.id === removedOption.id);
      
      if (wasApplied) {
        // Remove from both temp and applied states, and update filters immediately
        const updatedStates = selectedStates.filter(state => state.id !== removedOption.id);
        setSelectedStates(updatedStates);
        setTempSelectedStates(updatedStates);
        setFilters((prev) => ({
          ...prev,
          licensed_in: updatedStates.map((state) => state.id.toString()),
          page: 1,
        }));
      } else {
        // Just remove from temp selection (not yet applied)
        setTempSelectedStates(newValue);
      }
    } else if (reason === "clear") {
      // Handle clear button click (though we disabled it)
      setTempSelectedStates([]);
      setSelectedStates([]);
    } else {
      // Handle normal selection
      setTempSelectedStates(newValue);
    }
  };

  // Function to capture the paper ref
  const handlePaperRef = (node) => {
    if (node) {
      paperRef.current = node;
    }
  };

  return (
    <Filter
      label="Licensed In"
      icon={<MapPinIcon />}
      expandedDefault
      tooltip="Shows providers licensed in the selected state or authorized to practice in that state through PSYPACT."
    >
      <div ref={containerRef} style={{ position: "relative" }}>
        <Autocomplete
          multiple
          options={statesList}
          value={tempSelectedStates}
          onChange={handleTempChange}
          onOpen={handleOpen}
          onClose={() => {}} // Empty onClose to prevent auto-close
          open={isOpen}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disableCloseOnSelect
          disableClearable
          clearOnEscape={false}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox checked={selected} sx={{ marginRight: 1 }} />
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder={
                !tempSelectedStates?.length && !selectedStates?.length
                  ? "Select states"
                  : ""
              }
            />
          )}
          componentsProps={{
            paper: {
              ref: handlePaperRef,
              sx: {
                marginTop: "4px",
                paddingBottom: "50px", // Add space for the apply button
                position: "relative",
              },
            },
          }}
        />

        {/* Apply button - fixed at bottom right of dropdown */}
        {isOpen && tempSelectedStates.length > 0 && (
          <div
            style={{
              position: "absolute",
              bottom: "8px",
              right: "8px",
              zIndex: 1300,
            }}
          >
            <Button
              size="small"
              variant="contained"
              onClick={handleApply}
              sx={{
                minWidth: "60px",
                fontSize: "0.75rem",
                backgroundColor: "primary.main",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              }}
            >
              Apply
            </Button>
          </div>
        )}
      </div>
    </Filter>
  );
};

export default LicensedInFilter;
